<script lang="ts" setup>
import { computed, reactive, ref } from '#imports';
import { VueCropper } from 'vue-cropper';
import 'vue-cropper/dist/index.css';
import { useService } from '@kaokei/use-vue-service';
import { uploadAvatar } from '@core/api/common';
import editSvg from '@core/assets/image/icon/avatar/icon-edit.svg';
import { UserService } from '@core/services/user.service';
import { ZoneService } from '@core/services/zone.service';
import { showErrorMsg, showSuccessMsg } from '@core/utils/utility';

const ZOOM = {
  STEP: 2,
  MIN: 0,
  MAX: 100,
};

const props = defineProps({
  avatarSize: { type: Number, default: 60 },
  getCropContainer: { type: Function, default: undefined },
  cropSize: {
    type: Array<number>,
    default: [320, 320],
  },
  hoverText: { type: String, default: '' },
  editIconVisible: { type: Boolean, default: true },
  okText: { type: String, default: '确认' },
  cancelText: { type: String, default: '取消' },
});
const model = defineModel({ type: String, default: '' });
const emit = defineEmits(['crop-opened', 'crop-closed']);

const cropper = ref();
const loading = ref<boolean>(false);
const scaleInfo = reactive({
  min: 0,
  value: 0,
  sliderValue: 0,
});

const modalElRef = ref();
const cropInnerRef = ref();
const toCropImageBase64 = ref<string>('');
const cropperVisible = ref<boolean>(false);
const userService = useService(UserService);
const zoneService = useService(ZoneService);

const headers = computed(() => ({
  Authorization: userService.token,
  'Hetao-Oj-Zone': zoneService.zone,
}));
const handleCropCompletion = ref<((blob: Blob | Error) => void) | null>(null);

const beforeUpload = (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    startCropProcess(file);
    handleCropCompletion.value = (blobOrError) => {
      handleCropCompletion.value = null;
      if (blobOrError instanceof Error) {
        showErrorMsg(blobOrError.message);
        reject(blobOrError);
      } else {
        resolve(blobOrError);
      }
    };
  });
};

const startCropProcess = (file: File) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => {
    emit('crop-opened');
    toCropImageBase64.value = reader.result as string;
    cropperVisible.value = true;
  };
  reader.onerror = () => {
    handleCropCompletion.value?.(new Error('读取文件失败'));
  };
};

const customRequestHandler = (options: any) => {
  emit('crop-closed');
  loading.value = true;
  uploadAvatar(options.file)
    .then((res: any) => {
      const { url } = res;
      options.onSuccess(url, options.file);
      model.value = url;
      showSuccessMsg(`上传成功`);
    })
    .catch((err) => {
      // 请求失败
      options.onError(err, options.file);
      showErrorMsg(`上传失败`, err);
    })
    .finally(() => {
      loading.value = false;
    });
};
const getCropTarget = () => {
  // 优先使用外部提供的容器
  if (typeof props.getCropContainer === 'function') {
    const container = props.getCropContainer();
    if (container) return container;
  }

  // 使用内部模态框作为后备
  return modalElRef.value || document.body;
};

const cropTarget = computed(() => getCropTarget());
const handleZoomChange = (value: number) => {
  scaleInfo.sliderValue = value;
  if (scaleInfo.min === 0) {
    const currentScale = cropper.value?.scale as number;
    scaleInfo.min = currentScale;
    scaleInfo.value = currentScale;
  }
  const scale = value / 100;
  if (cropper.value) {
    const newScale = scaleInfo.min + scale;
    scaleInfo.value = newScale;
    cropper.value.checkoutImgAxis(cropper.value.x, cropper.value.y, newScale);
    cropper.value.scale = newScale;
  }
};

const adjustZoom = (delta: number) => {
  const newValue = Math.max(
    ZOOM.MIN,
    Math.min(ZOOM.MAX, scaleInfo.sliderValue + delta),
  );

  if (newValue !== scaleInfo.sliderValue) {
    handleZoomChange(newValue);
  }
};

const zoomIn = () => adjustZoom(ZOOM.STEP);
const zoomOut = () => adjustZoom(-ZOOM.STEP);

const resetCropState = () => {
  // 2. 清除图片数据
  toCropImageBase64.value = '';

  // 3. 重置缩放状态
  scaleInfo.min = 0;
  scaleInfo.value = 0;
  scaleInfo.sliderValue = 0;

  // 4. 隐藏界面
  cropperVisible.value = false;
  handleCropCompletion.value = null;
};

const handleCropOk = () => {
  try {
    cropper.value.getCropBlob((data: Blob) => {
      const blobOrError = data === null ? new Error('裁剪失败') : data;
      handleCropCompletion.value?.(blobOrError);
      resetCropState();
    });
  } catch (error) {
    console.error('Crop failed', error);
    resetCropState();
  }
};
const cancelCrop = () => {
  emit('crop-closed');
  resetCropState();
};

const cropConfig = computed(() => {
  const [width, height] = props.cropSize;
  const extraWidth = 4; // 明确4px的额外覆盖
  return {
    width: `${width}px`,
    height: `${height}px`,
    overlay: {
      shadowWidth: width + extraWidth,
    },
  };
});
const getTooltipPopupContainer = () => cropInnerRef.value;
const usingInternalModal = computed(() => {
  return !props.getCropContainer;
});
</script>

<template>
  <a-upload
    name="image"
    :show-upload-list="false"
    :headers="headers"
    accept=".jpg, .jpeg, .png, .svg"
    :before-upload="beforeUpload"
    :custom-request="customRequestHandler"
    :max-count="1"
  >
    <div class="group relative">
      <a-spin :spinning="loading" size="small">
        <a-avatar
          :size="avatarSize"
          class="!border-zinc-100"
          :src="model"
          alt="avatar"
        />
      </a-spin>
      <span
        v-if="hoverText"
        class="absolute left-0 top-0 z-[1] hidden h-full w-full items-center justify-center text-nowrap rounded-full bg-[rgba(0,0,0,.7)] text-[#fff] group-hover:flex"
        >{{ hoverText }}</span
      >
      <span
        v-if="editIconVisible"
        class="flex-center absolute bottom-0 right-0 z-10 h-7 w-7 rounded-full border border-white bg-[#F5F0FF]"
        ><img :src="editSvg" alt="edit"
      /></span>
      <slot></slot>
    </div>
  </a-upload>

  <Teleport v-if="cropperVisible" :to="cropTarget">
    <div
      ref="cropInnerRef"
      class="relative box-content flex flex-col items-center px-6 pb-8 pt-[18px]"
      :style="{
        width: cropConfig.width,
      }"
    >
      <img
        class="absolute right-5 top-5 cursor-pointer"
        src="@core/assets/image/icon/avatar/icon-close.svg"
        alt="close"
        @click="cancelCrop"
      />
      <h6 class="text-colorText mb-5 text-base font-bold">调整头像尺寸</h6>
      <div
        class="relative overflow-hidden"
        :style="{
          height: `${cropConfig.height}`,
          width: `${cropConfig.width}`,
        }"
      >
        <VueCropper
          ref="cropper"
          :img="toCropImageBase64"
          :output-size="0.5"
          :output-type="'png'"
          :auto-crop-width="cropSize[0]"
          :auto-crop-height="cropSize[1]"
          :can-move-box="false"
          auto-crop
          fixed-box
          fixed
          :info="false"
          mode="cover"
          :can-scale="true"
          :center-box="true"
          :high="false"
        ></VueCropper>
        <div
          :style="{
            boxShadow: `0 0 0 ${cropConfig.overlay.shadowWidth}px rgba(0, 0, 0, 0.5)`,
          }"
          class="pointer-events-none absolute bottom-0 left-0 right-0 top-0 rounded-full"
        ></div>
      </div>

      <div class="my-[17px] flex h-[34px] w-full items-center">
        <img
          class="mr-4 w-4 cursor-pointer opacity-70 transition-all hover:opacity-100"
          src="@core/assets/image/icon/avatar/icon-minus.svg"
          alt="minus"
          @click="zoomOut"
        />
        <a-form-item-rest>
          <a-slider
            v-model:value="scaleInfo.sliderValue"
            :range="false"
            class="flex-1"
            :get-tooltip-popup-container="getTooltipPopupContainer"
            @change="handleZoomChange($event as number)"
          />
        </a-form-item-rest>
        <img
          class="ml-4 w-4 cursor-pointer opacity-70 transition-all hover:opacity-100"
          src="@core/assets/image/icon/avatar/icon-plus.svg"
          alt="minus"
          @click="zoomIn"
        />
      </div>

      <div class="flex items-center justify-center gap-3">
        <a-button class="min-w-[80px]" @click="cancelCrop">{{
          cancelText
        }}</a-button>
        <a-button class="min-w-[80px]" type="primary" @click="handleCropOk">{{
          okText
        }}</a-button>
      </div>
    </div>
  </Teleport>
  <a-modal
    v-if="usingInternalModal"
    v-model:open="cropperVisible"
    width="368px"
    :footer="null"
    :closable="false"
    :mask-closable="false"
    :destroy-on-close="true"
    :body-style="{ padding: '0' }"
    :centered="true"
  >
    <div ref="modalElRef"></div>
  </a-modal>
</template>
<style scoped>
:deep(.ant-slider-horizontal .ant-slider-rail) {
  @apply bg-colorFillSecondary;
}
:deep(.ant-slider .ant-slider-track) {
  @apply bg-primary rounded;
}
:deep(.ant-slider:hover .ant-slider-track) {
  @apply bg-primary;
}
:deep(.ant-slider .ant-slider-handle.ant-slider-handle::after) {
  @apply shadow-[0_0_0_2px_var(--primary)];
}
:deep(.ant-slider .ant-slider-handle:hover::after) {
  @apply shadow-[0_0_0_4px_var(--primary)];
}
:deep(.ant-tooltip .ant-tooltip-inner.ant-tooltip-inner) {
  @apply text-center;
}
</style>
